import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "../ui/tooltip";
import {
  Shield,
  Play,
  AlertTriangle,
  AlertCircle,
  Info,
  ChevronDown,
  Loader2,
  CheckCircle2,
  FileSearch,
  ArrowRight
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Translations
const translations = {
  nb: {
    selectPlaybook: "Velg playbook...",
    runAudit: "Kjør revisjon",
    analyzing: "Analyserer...",
    documentAudit: "Dokumentrevisjon",
    auditDescription: "Velg en playbook og kjør revisjon for å identifisere potensielle problemer",
    auditComplete: "Revisjon fullført",
    findingsIdentified: "funn identifisert",
    selectPlaybookFirst: "Velg en playbook først",
    high: "Høy",
    medium: "Medium", 
    low: "Lav",
    noFindings: "Ingen funn",
    priority: "prioritet",
    recommendation: "Anbefaling:",
    use: "Bruk",
    insertDirectly: "Sett inn direkte",
    withTracking: "Med endringssporing"
  },
  en: {
    selectPlaybook: "Select playbook...",
    runAudit: "Run audit",
    analyzing: "Analyzing...",
    documentAudit: "Document Audit",
    auditDescription: "Select a playbook and run an audit to identify potential issues",
    auditComplete: "Audit complete",
    findingsIdentified: "findings identified",
    selectPlaybookFirst: "Select a playbook first",
    high: "High",
    medium: "Medium",
    low: "Low",
    noFindings: "No findings",
    priority: "priority",
    recommendation: "Recommendation:",
    use: "Use",
    insertDirectly: "Insert directly",
    withTracking: "With change tracking"
  }
};

export const AuditTab = ({ onAudit, onApply, loading, token, language = 'nb' }) => {
  const [playbooks, setPlaybooks] = useState([]);
  const [selectedPlaybook, setSelectedPlaybook] = useState("");
  const [auditResult, setAuditResult] = useState(null);
  const [runningAudit, setRunningAudit] = useState(false);
  
  const t = translations[language] || translations.nb;

  useEffect(() => {
    const fetchPlaybooks = async () => {
      try {
        const response = await axios.get(`${API}/word-addin/playbooks`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPlaybooks(response.data.playbooks || []);
        if (response.data.playbooks?.length > 0) {
          setSelectedPlaybook(response.data.playbooks[0].id);
        }
      } catch (error) {
        console.error("Fetch playbooks error:", error);
      }
    };
    if (token) fetchPlaybooks();
  }, [token]);

  const handleRunAudit = async () => {
    if (!selectedPlaybook) {
      toast.error(t.selectPlaybookFirst);
      return;
    }

    setRunningAudit(true);
    const result = await onAudit(selectedPlaybook);
    setRunningAudit(false);

    if (result) {
      setAuditResult(result);
      toast.success(t.auditComplete, {
        description: `${result.findings?.length || 0} ${t.findingsIdentified}`
      });
    }
  };

  const getSeverityConfig = (severity) => {
    switch (severity) {
      case "high":
        return {
          icon: AlertTriangle,
          label: t.high,
          className: "bg-red-100 text-red-700 border-red-200"
        };
      case "medium":
        return {
          icon: AlertCircle,
          label: t.medium,
          className: "bg-amber-100 text-amber-700 border-amber-200"
        };
      case "low":
        return {
          icon: Info,
          label: t.low,
          className: "bg-blue-100 text-blue-700 border-blue-200"
        };
      default:
        return {
          icon: Info,
          label: severity,
          className: "bg-slate-100 text-slate-600"
        };
    }
  };

  const groupedFindings = auditResult?.findings?.reduce((acc, finding) => {
    const severity = finding.severity || "medium";
    if (!acc[severity]) acc[severity] = [];
    acc[severity].push(finding);
    return acc;
  }, {}) || {};

  return (
    <TooltipProvider>
      <div className="flex-1 flex flex-col h-full bg-white">
        {/* Controls */}
        <div className="px-4 py-3 border-b border-slate-100 space-y-3 bg-slate-50">
          <Select
            value={selectedPlaybook}
            onValueChange={setSelectedPlaybook}
          >
            <SelectTrigger className="h-10 bg-white border-slate-200 rounded-xl" data-testid="playbook-selector">
              <Shield className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder={t.selectPlaybook} />
            </SelectTrigger>
            <SelectContent>
              {playbooks.map((pb) => (
                <SelectItem key={pb.id} value={pb.id}>
                  <div className="flex flex-col items-start">
                    <span className="font-medium">{pb.name}</span>
                    <span className="text-xs text-slate-500">{pb.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button
            onClick={handleRunAudit}
            disabled={runningAudit || loading || !selectedPlaybook}
            className="w-full h-10 bg-slate-900 hover:bg-slate-800 rounded-xl"
            data-testid="run-audit-button"
          >
            {runningAudit ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t.analyzing}
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                {t.runAudit}
              </>
            )}
          </Button>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto" data-testid="audit-results">
          {!auditResult ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-4">
              <div className="w-14 h-14 rounded-2xl bg-violet-100 flex items-center justify-center mb-4">
                <FileSearch className="w-7 h-7 text-violet-600" />
              </div>
              <h3 className="font-semibold text-base text-slate-900 mb-1">{t.documentAudit}</h3>
              <p className="text-sm text-slate-500 max-w-[240px]">
                {t.auditDescription}
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {/* Summary */}
              <Card className="overflow-hidden border-slate-200 shadow-sm bg-gradient-to-br from-white to-slate-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="w-4 h-4 text-violet-600" />
                    <span className="font-semibold text-sm text-slate-900">{auditResult.playbook_name}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    {auditResult.summary.high_count > 0 && (
                      <Badge className={`${getSeverityConfig("high").className} text-[11px] px-2 py-0.5`}>
                        {auditResult.summary.high_count} {t.high.toLowerCase()}
                      </Badge>
                    )}
                    {auditResult.summary.medium_count > 0 && (
                      <Badge className={`${getSeverityConfig("medium").className} text-[11px] px-2 py-0.5`}>
                        {auditResult.summary.medium_count} {t.medium.toLowerCase()}
                      </Badge>
                    )}
                    {auditResult.summary.low_count > 0 && (
                      <Badge className={`${getSeverityConfig("low").className} text-[11px] px-2 py-0.5`}>
                        {auditResult.summary.low_count} {t.low.toLowerCase()}
                      </Badge>
                    )}
                    {auditResult.findings?.length === 0 && (
                      <Badge className="bg-emerald-100 text-emerald-700 text-[11px] px-2 py-0.5">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        {t.noFindings}
                      </Badge>
                    )}
                  </div>
                  
                  {auditResult.summary.overall_assessment && (
                    <p className="text-xs text-slate-500 leading-relaxed">
                      {auditResult.summary.overall_assessment}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Findings */}
              {["high", "medium", "low"].map((severity) => {
                const findings = groupedFindings[severity] || [];
                if (findings.length === 0) return null;

                const config = getSeverityConfig(severity);
                const Icon = config.icon;

                return (
                  <div key={severity} className="space-y-2" data-testid={`audit-group-${severity}`}>
                    <div className="flex items-center gap-2 px-1">
                      <Icon className="w-3.5 h-3.5 text-slate-500" />
                      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                        {config.label} {t.priority}
                      </span>
                    </div>

                    {findings.map((finding, idx) => (
                      <Card key={finding.id || idx} className="overflow-hidden border-slate-200 shadow-sm" data-testid={`audit-item-${severity}-${idx}`}>
                        <CardContent className="p-0">
                          <div className="flex items-start justify-between p-3 border-b border-slate-100">
                            <h4 className="font-medium text-sm text-slate-900 pr-2">{finding.title}</h4>
                            <Badge className={`${config.className} shrink-0 text-[10px] px-1.5 py-0`} data-testid={`sev-badge-${severity}`}>
                              {config.label}
                            </Badge>
                          </div>
                          
                          <div className="p-3 space-y-3">
                            <p className="text-xs text-slate-600 leading-relaxed">
                              {finding.description}
                            </p>

                            {finding.recommendation && (
                              <div className="rounded-xl bg-slate-50 p-2.5">
                                <p className="text-[11px] font-medium text-slate-500 mb-1">{t.recommendation}</p>
                                <p className="text-xs text-slate-700 leading-relaxed">{finding.recommendation}</p>
                              </div>
                            )}

                            <div className="flex items-center justify-end gap-2 pt-1">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size="sm" variant="secondary" className="h-7 text-xs bg-slate-100 hover:bg-slate-200">
                                    {t.use}
                                    <ChevronDown className="w-3 h-3 ml-1" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => onApply({ 
                                      content: finding.recommendation,
                                      rationale: finding.rationale,
                                      prompt_id: finding.id
                                    }, "direct")}
                                  >
                                    {t.insertDirectly}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => onApply({
                                      content: finding.recommendation,
                                      rationale: finding.rationale,
                                      prompt_id: finding.id
                                    }, "tracked")}
                                  >
                                    {t.withTracking}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};
export default AuditTab;
